import { useEffect, useRef, type MutableRefObject } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";

import {
  cameraForward,
  fittedCameraQuaternion,
  photoPlaneSize,
} from "./camera";
import { photoOpacity, sampleTour } from "./tour";
import type { PresentationScene, ScenePhoto } from "./types";

const EDGE_COLORS: Record<string, number> = {
  edge_0: 0xff0000,
  edge_1: 0x00ff00,
  edge_2: 0x0000ff,
  edge_3: 0xff00ff,
};

interface SceneViewProps {
  data: PresentationScene;
  playing: boolean;
  elapsedRef: MutableRefObject<number>;
  onTourUpdate: (photoIndex: number, phase: string) => void;
}

interface PhotoObject {
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  material: THREE.MeshBasicMaterial;
  frame: THREE.LineSegments;
}

function makePhotoGeometry(
  photo: ScenePhoto,
  distance: number,
): THREE.BufferGeometry {
  const [width, height] = photoPlaneSize(photo, distance);
  const principal = photo.camera.principalPoint;
  const left = -width * principal[0];
  const right = width * (1 - principal[0]);
  const top = height * principal[1];
  const bottom = -height * (1 - principal[1]);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(
      [
        left,
        top,
        -distance,
        right,
        top,
        -distance,
        right,
        bottom,
        -distance,
        left,
        bottom,
        -distance,
      ],
      3,
    ),
  );
  geometry.setAttribute(
    "uv",
    new THREE.Float32BufferAttribute([0, 1, 1, 1, 1, 0, 0, 0], 2),
  );
  geometry.setIndex([0, 2, 1, 0, 3, 2]);
  geometry.computeVertexNormals();
  return geometry;
}

function makeModel(data: PresentationScene): THREE.Group {
  const group = new THREE.Group();
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(data.model.vertices.flat(), 3),
  );
  geometry.setIndex(data.model.faces.flat());
  geometry.computeVertexNormals();
  const material = new THREE.MeshStandardMaterial({
    color: 0xb8b5aa,
    roughness: 0.92,
    metalness: 0.02,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);

  Object.entries(data.model.edges).forEach(([edgeId, points]) => {
    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(points.flat());
    const line = new Line2(
      lineGeometry,
      new LineMaterial({
        color: EDGE_COLORS[edgeId] ?? 0xf2eee5,
        linewidth: 4,
        transparent: true,
        opacity: 0.95,
      }),
    );
    line.computeLineDistances();
    group.add(line);
  });
  return group;
}

function makeFloor(
  data: PresentationScene,
  floorZ: number,
  textureLoader: THREE.TextureLoader,
  maxAnisotropy: number,
  fallbackRadius: number,
): THREE.Mesh {
  if (!data.map) {
    const fallback = new THREE.Mesh(
      new THREE.CircleGeometry(fallbackRadius, 96),
      new THREE.MeshStandardMaterial({ color: 0x121716, roughness: 1 }),
    );
    fallback.position.set(0, 0, floorZ);
    return fallback;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(
      data.map.corners.flatMap(([x, y]) => [x, y, floorZ]),
      3,
    ),
  );
  geometry.setAttribute(
    "uv",
    new THREE.Float32BufferAttribute([0, 1, 1, 1, 1, 0, 0, 0], 2),
  );
  geometry.setIndex([0, 2, 1, 0, 3, 2]);
  geometry.computeVertexNormals();
  const texture = textureLoader.load(
    new URL(data.map.image, document.baseURI).href,
  );
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.anisotropy = maxAnisotropy;
  const floor = new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      map: texture,
      color: 0xffffff,
      fog: true,
      toneMapped: false,
    }),
  );
  floor.receiveShadow = true;
  return floor;
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
      child.geometry.dispose();
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      materials.forEach((material) => {
        if (material instanceof THREE.MeshBasicMaterial && material.map)
          material.map.dispose();
        material.dispose();
      });
    }
  });
}

export default function SceneView({
  data,
  playing,
  elapsedRef,
  onTourUpdate,
}: SceneViewProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playingRef = useRef(playing);
  const updateRef = useRef(onTourUpdate);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);
  useEffect(() => {
    updateRef.current = onTourUpdate;
  }, [onTourUpdate]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x090b0b);
    scene.fog = new THREE.FogExp2(0x090b0b, 0.012);
    const camera = new THREE.PerspectiveCamera(45, 1, 0.03, 500);
    camera.up.set(0, 0, 1);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    host.appendChild(renderer.domElement);

    const bounds = new THREE.Box3(
      new THREE.Vector3().fromArray(data.model.bounds[0]),
      new THREE.Vector3().fromArray(data.model.bounds[1]),
    );
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const model = makeModel(data);
    scene.add(model);
    const textureLoader = new THREE.TextureLoader();

    scene.add(new THREE.HemisphereLight(0xece8dc, 0x232a2c, 2.1));
    const key = new THREE.DirectionalLight(0xfff3da, 4.2);
    key.position.copy(center).add(new THREE.Vector3(-12, -16, 20));
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 80;
    key.shadow.camera.left = -15;
    key.shadow.camera.right = 15;
    key.shadow.camera.top = 15;
    key.shadow.camera.bottom = -15;
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x91abc0, 2.4);
    rim.position.copy(center).add(new THREE.Vector3(15, 10, 4));
    scene.add(rim);

    const floorZ =
      Math.min(0, ...data.photos.map((photo) => photo.camera.position[2])) - 4;
    const floor = makeFloor(
      data,
      floorZ,
      textureLoader,
      renderer.capabilities.getMaxAnisotropy(),
      Math.max(size.x, size.y) * 3.6,
    );
    scene.add(floor);
    const photoObjects: PhotoObject[] = data.photos.map((photo) => {
      const fittedPosition = new THREE.Vector3().fromArray(
        photo.camera.position,
      );
      const depth = Math.max(
        1,
        center.clone().sub(fittedPosition).dot(cameraForward(photo.camera)),
      );
      const distance = THREE.MathUtils.clamp(
        depth * 0.42,
        1.8,
        Math.max(2, depth * 0.72),
      );
      const texture = textureLoader.load(
        new URL(photo.image, document.baseURI).href,
      );
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 0.14,
        side: THREE.DoubleSide,
        depthWrite: false,
        toneMapped: false,
      });
      const geometry = makePhotoGeometry(photo, distance);
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.copy(fittedPosition);
      mesh.quaternion.copy(fittedCameraQuaternion(photo.camera));
      mesh.renderOrder = 20;
      scene.add(mesh);

      const frame = new THREE.LineSegments(
        new THREE.EdgesGeometry(geometry),
        new THREE.LineBasicMaterial({
          color: 0xe8dfc9,
          transparent: true,
          opacity: 0.2,
        }),
      );
      frame.position.copy(mesh.position);
      frame.quaternion.copy(mesh.quaternion);
      frame.renderOrder = 21;
      scene.add(frame);
      return { mesh, material, frame };
    });

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(center);
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.enabled = !playingRef.current;
    controls.minDistance = Math.max(size.length() * 0.35, 2);
    controls.maxDistance = 90;

    let animationFrame = 0;
    let previousTime = performance.now();
    let lastStatus = "";
    const animate = (now: number) => {
      const delta = Math.min((now - previousTime) / 1000, 0.1);
      previousTime = now;
      controls.enabled = !playingRef.current;
      if (playingRef.current) {
        elapsedRef.current += delta;
        const sample = sampleTour(
          elapsedRef.current,
          data.photos,
          center,
          Math.max(host.clientWidth, 1) / Math.max(host.clientHeight, 1),
        );
        camera.position.copy(sample.position);
        camera.quaternion.copy(sample.quaternion);
        camera.fov = sample.fov;
        camera.aspect =
          Math.max(host.clientWidth, 1) / Math.max(host.clientHeight, 1);
        camera.updateProjectionMatrix();

        photoObjects.forEach((photoObject, index) => {
          const opacity = photoOpacity(sample, index);
          photoObject.material.opacity = opacity;
          (photoObject.frame.material as THREE.LineBasicMaterial).opacity =
            opacity * 0.55;
        });
        const status = `${sample.photoIndex}:${sample.phase}`;
        if (status !== lastStatus) {
          updateRef.current(sample.photoIndex, sample.phase);
          lastStatus = status;
        }
      } else {
        controls.update();
      }
      renderer.render(scene, camera);
      animationFrame = requestAnimationFrame(animate);
    };

    const resize = () => {
      const width = Math.max(host.clientWidth, 1);
      const height = Math.max(host.clientHeight, 1);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      model.traverse((child) => {
        if (child instanceof Line2)
          child.material.resolution.set(width, height);
      });
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();
    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      controls.dispose();
      photoObjects.forEach(({ mesh, frame }) => {
        scene.remove(mesh, frame);
        disposeObject(mesh);
        disposeObject(frame);
      });
      scene.remove(model, floor);
      disposeObject(model);
      disposeObject(floor);
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [data, elapsedRef]);

  return (
    <div
      className="scene-view"
      ref={hostRef}
      aria-label="3D rekonstrukce plastiky Rovnováha"
    />
  );
}
